import {
  activateAccount,
  attachUserDetails,
  attachUserId,
  logout,
  setRegion,
  signIn,
  updateAuthenticationContext,
} from '../store/actions/auth.actions';
import { ServiceBase } from './service.base';
import AuthenticationServiceApi from '../api/authentication.service.api';
import {
  GENERIC_SERVER_ERROR_MESSAGE,
  ORGANIZATION_UNAUTHORIZED,
  USER_DEACTIVATED,
} from '../constants/applicationErrorCodes';
import { navigate } from './navigation.service';
import {
  ROUTES,
  DEVICE_FLAGS,
  AUTHENTICATION_TYPE,
  SIGN_OUT_CODES,
  SIGN_IN_FLOW_ERROR_CODE,
} from '../constants';
import { APPLICATION_STEPS } from '../constants/applicationSteps';
import { finishStep } from '../store/actions/application.actions';
import userServiceApi from '../api/user.service.api';
import API from '../api/API';
import authService, { REFRESH_RESULT } from './auth.service';
import { currentEnvironment } from './Environment';
import { switchOrganization, updateOrganization } from '../store/actions/organization.actions';
import authenticationServiceApi from '../api/authentication.service.api';
import _ from 'lodash';
import Logger from '../utils/Logger';

class UserService extends ServiceBase {
  constructor(props) {
    super(props);
    this.reducer = 'auth';
    this.logger = Logger.create({ loggerName: 'UserService' });
  }

  /**
   * Signin user
   * @param {*} { email }
   * @returns
   * @memberof UserService
   */
  async signin({ email, navigateToOtp = true }) {
    try {
      const { region } = await this._selectRegion({
        email,
      });

      if (!region) {
        return { error: 'NO_REGION' };
      }

      this.dispatch(setRegion({ region }));
    } catch (e) {
      return { error: e.message };
    }

    const { error, data } = await AuthenticationServiceApi.loginWithEmail({
      email,
    });

    if (error) {
      if (data.code === ORGANIZATION_UNAUTHORIZED) {
        return { error: 'ORGANIZATION_UNAUTHORIZED' };
      }

      if (data.code === USER_DEACTIVATED) {
        const lang = this.storeState.lang['WELCOME_SCREEN'];
        return { error: lang.USER_DEACTIVATED };
      }
      return { error: GENERIC_SERVER_ERROR_MESSAGE };
    }

    const { id, authType = AUTHENTICATION_TYPE.OTP } = data;
    // Attach user ID
    this.dispatch(attachUserDetails({ email }));
    this.dispatch(attachUserId({ id }));

    if (authType === AUTHENTICATION_TYPE.OTP && navigateToOtp) {
      // User have created an OTP
      navigate(ROUTES.OTP_VERIFICATION, { email });
    }

    return data;
  }

  // Dispatches setRegion on success, then attachUserDetails + attachUserId
  // once the user is identified. Callers should not re-dispatch these.
  async resolveSignInFlow({ email }) {
    try {
      const { region } = await this._selectRegion({ email });

      if (!region) {
        return { error: SIGN_IN_FLOW_ERROR_CODE.NO_REGION };
      }

      this.dispatch(setRegion({ region }));
    } catch (e) {
      this.logger.error('resolveSignInFlow: region selection failed', {
        message: e?.message,
      });
      return { error: SIGN_IN_FLOW_ERROR_CODE.GENERIC_SERVER_ERROR };
    }

    const { error, data } = await AuthenticationServiceApi.loginWithEmail({ email });

    if (error) {
      if (data?.code === ORGANIZATION_UNAUTHORIZED) {
        return { error: SIGN_IN_FLOW_ERROR_CODE.ORGANIZATION_UNAUTHORIZED };
      }
      if (data?.code === USER_DEACTIVATED) {
        return { error: SIGN_IN_FLOW_ERROR_CODE.USER_DEACTIVATED };
      }
      this.logger.warn('resolveSignInFlow: loginWithEmail failed with unmapped code', {
        errorCode: data?.code,
        status: error?.response?.status,
        message: error?.message,
      });
      return { error: SIGN_IN_FLOW_ERROR_CODE.GENERIC_SERVER_ERROR };
    }

    const { id, authType = AUTHENTICATION_TYPE.OTP, payload } = data;

    this.dispatch(attachUserDetails({ email }));
    this.dispatch(attachUserId({ id }));

    if (authType === AUTHENTICATION_TYPE.SAML) {
      const { authenticationMethodId, organizationId } = payload || {};
      if (!authenticationMethodId || !organizationId) {
        return { error: SIGN_IN_FLOW_ERROR_CODE.SSO_MISCONFIGURED };
      }
      return {
        authType: AUTHENTICATION_TYPE.SAML,
        authenticationMethodId,
        organizationId,
        loginHint: email,
      };
    }

    if (authType === AUTHENTICATION_TYPE.OAUTH) {
      return {
        authType: AUTHENTICATION_TYPE.OAUTH,
        provider: payload?.provider,
        loginHint: email,
      };
    }

    return { authType: AUTHENTICATION_TYPE.OTP, userId: id, email };
  }

  /**
   * Resolve the sign-in flow for a given email. On success, returns an object
   * whose `authType` (from AUTHENTICATION_TYPE) tells the caller which flow to
   * drive. On failure, returns `{ error }` with a code from SIGN_IN_FLOW_ERROR_CODE.
   *
   * Success shapes:
   *   { authType: SAML,  authenticationMethodId, organizationId, loginHint }
   *   { authType: OAUTH, provider, loginHint }
   *   { authType: OTP,   userId, email }
   *
   * Failure shape:
   *   { error: <SIGN_IN_FLOW_ERROR_CODE> }
   *
   * @param {{ email: string }} params
   * @returns {Promise<object>}
   * @memberof UserService
   */
  async resolveSignInFlow({ email }) {
    try {
      const { region } = await this._selectRegion({ email });

      if (!region) {
        return { error: SIGN_IN_FLOW_ERROR_CODE.NO_REGION };
      }

      this.dispatch(setRegion({ region }));
    } catch (e) {
      return { error: SIGN_IN_FLOW_ERROR_CODE.GENERIC_SERVER_ERROR };
    }

    const { error, data } = await AuthenticationServiceApi.loginWithEmail({ email });

    if (error) {
      if (data?.code === ORGANIZATION_UNAUTHORIZED) {
        return { error: SIGN_IN_FLOW_ERROR_CODE.ORGANIZATION_UNAUTHORIZED };
      }
      if (data?.code === USER_DEACTIVATED) {
        return { error: SIGN_IN_FLOW_ERROR_CODE.USER_DEACTIVATED };
      }
      return { error: SIGN_IN_FLOW_ERROR_CODE.GENERIC_SERVER_ERROR };
    }

    const { id, authType = AUTHENTICATION_TYPE.OTP, payload } = data;

    this.dispatch(attachUserDetails({ email }));
    this.dispatch(attachUserId({ id }));

    if (authType === AUTHENTICATION_TYPE.SAML) {
      const { authenticationMethodId, organizationId } = payload || {};
      if (!authenticationMethodId || !organizationId) {
        return {
          error: SIGN_IN_FLOW_ERROR_CODE.SSO_MISCONFIGURED,
          authType: AUTHENTICATION_TYPE.SAML,
        };
      }
      return {
        authType: AUTHENTICATION_TYPE.SAML,
        authenticationMethodId,
        organizationId,
        loginHint: email,
      };
    }

    if (authType === AUTHENTICATION_TYPE.OAUTH) {
      return {
        authType: AUTHENTICATION_TYPE.OAUTH,
        provider: payload?.provider,
        loginHint: email,
      };
    }

    return { authType: AUTHENTICATION_TYPE.OTP, userId: id, email };
  }

  /**
   * Contact us
   * @param {*} { email, fullName, phone, organization, details }
   * @returns
   * @memberof UserService
   */
  async contactUs({ email, fullName, organization, details }) {
    let { error, data } = await AuthenticationServiceApi.contactUs({
      email,
      fullName,
      organization,
      details,
    });

    if (error) {
      return { error: data.message };
    }

    return data;
  }

  /**
   * Verify OTP code and sign if needed
   * @param {*} { code }
   * @returns
   * @memberof UserService
   */
  async verifyCode({ code, userId }) {
    let { error, data } = await AuthenticationServiceApi.verifyCode({
      code,
      userId: userId || this.state.id,
    });

    if (error) {
      this.logger.warn(`Fail to verify code [${code}] ${data.message}`);
      return { error: data.message, code: data.code };
    }

    this.logger.info('Signin (verification code) - Succeeded', { userId: userId || this.state.id });
    // Data include token and user
    this.dispatch(signIn(data));

    return {};
  }

  /**
   * Signin with oauth
   * @param {*} { provider: String, codeVerifier: String, redirectUri:String, clientId: String, code: String }
   * @returns
   * @memberof UserService
   */
  async oauthLogin({ provider, codeVerifier, redirectUri, clientId, code }) {
    let { error, data } = await AuthenticationServiceApi.oauthVerification({
      provider,
      codeVerifier,
      redirectUri,
      clientId,
      code,
    });

    if (error) {
      if (data.code === ORGANIZATION_UNAUTHORIZED) {
        navigate(ROUTES.NOT_ALLOWED, { email: '' });
        return { error: '' };
      }
      return { error: GENERIC_SERVER_ERROR_MESSAGE };
    }

    this.logger.log('Signin (OAuth) - Succeeded', { clientId });
    // Data include token and user
    this.dispatch(signIn(data));

    return {};
  }

  /**
   * Activate oauth organization
   * @param {*} { provider: String, codeVerifier?: String, redirectUri:String, clientId: String, code: String }
   * @returns
   * @memberof UserService
   */

  async activateOauth({ provider, codeVerifier, redirectUri, clientId, code }) {
    let { error, data } = await AuthenticationServiceApi.activateOAuthAccount({
      provider,
      codeVerifier,
      redirectUri,
      clientId,
      code,
    });

    if (error) {
      if (data.code === ORGANIZATION_UNAUTHORIZED) {
        navigate(ROUTES.NOT_ALLOWED, { email: '' });
        return { error: '' };
      }
      return { error: GENERIC_SERVER_ERROR_MESSAGE };
    }

    this.dispatch(switchOrganization({ id: data.organizationId }));
    this.dispatch(activateAccount());

    return data;
  }

  /**
   *
   *
   * @returns
   * @memberof UserService
   */
  async init() {
    this.logger.info('Initializing authentication process', {
      userId: this.userId,
      organizationId: this.organizationId,
      refreshTokenDetails: this.state?.refreshTokenDetails,
      accessTokenDetails: this.state?.tokenDetails,
    });

    try {
      const isReady = await this._initByPlatform();

      if (isReady) {
        await this._userInit();
      }
    } catch (error) {
      this.logger.error('Authentication init failed', {
        error: error?.message,
        stack: error?.stack,
      });
    }

    this._finishAuthStep();
  }

  async _initByPlatform() {
    if (global.platform === 'web') {
      return await this._webInit();
    }
    return await this._mobileInit();
  }

  async _userInit() {
    const { error, data } = await userServiceApi.me();

    if (error) {
      this.logger.warn('getting user details failed', {
        errorCode: error.response?.status,
        message: error.message,
      });

      if (error.response?.status === 401) {
        this._forceLogout({ skipApiCall: true, signOutCode: SIGN_OUT_CODES.UNAUTHORIZED });
      }
      return;
    }

    this.logger.info('user details fetched successfully - user is authenticated', {
      userId: data.user.id,
    });

    // Attach user details
    this.dispatch(attachUserDetails(data.user));

    // Fetch user's organization
    this.dispatch(updateOrganization());
  }

  async _mobileInit() {
    if (!this.isAuthenticated) {
      this.logger.info('user has no valid token');

      // Check if user has refresh token - if has, force local logout
      if (this.refreshToken) {
        this.logger.warn(
          'User about to logout locally - has refresh token, but not authenticated',
          {
            signOutCode: SIGN_OUT_CODES.SESSION_EXPIRED,
          },
        );

        this._forceLogout({ skipApiCall: true, signOutCode: SIGN_OUT_CODES.SESSION_EXPIRED });
      }

      const isOnboardingFinished = this.storeState.deviceFlags[DEVICE_FLAGS.ONBOARDING_FINISHED];
      if (!_.isUndefined(isOnboardingFinished) && !isOnboardingFinished) {
        navigate(ROUTES.ONBOARDING);
      }
      return false;
    }

    const refreshResult = await this._refreshAccessTokenThatIsExpiringSoon();

    if (refreshResult === REFRESH_RESULT.AUTH_FAILED) {
      this.logger.warn('failed to refresh access token - user logout locally', {
        signOutCode: SIGN_OUT_CODES.TOKEN_REFRESH_FAILED,
      });

      this.dispatch(
        logout({ skipApiCall: true, signOutCode: SIGN_OUT_CODES.TOKEN_REFRESH_FAILED }),
      );
      return false;
    }

    if (refreshResult === REFRESH_RESULT.NETWORK_ERROR) {
      // Keep user logged in for offline mode — refresh will be retried on next app open
      return false;
    }

    return true;
  }

  // In web, we use server side cookies for auth.
  // Because of that, the best way to test if the user is logged in is to refresh the access token
  async _webInit() {
    const authContext = await authenticationServiceApi.authContext();

    if (authContext.success) {
      this.dispatch(updateAuthenticationContext(authContext.data));
      return true;
    }

    this.logger.info('auth context request failed - will try to refresh token');

    const refreshResult = await authService.refreshToken();

    if (refreshResult === REFRESH_RESULT.SUCCESS) {
      return true;
    }

    if (refreshResult === REFRESH_RESULT.NETWORK_ERROR) {
      this.logger.warn('token refresh failed due to network error - keeping session', {
        userId: this.userId,
      });
      return false;
    }

    const signOutCode = this.state?.id ? SIGN_OUT_CODES.TOKEN_REFRESH_FAILED : undefined;

    this.logger.warn('token refresh failed - user will be logged out', {
      signOutCode,
      userId: this.userId,
      organizationId: this.state?.organization?.id || this.state?.auth?.organizationId,
    });

    this.dispatch(logout({ skipApiCall: true, signOutCode }));
    return false;
  }

  /**
   * Select allowed region for authentication and further requests
   * @param {*} { email }
   * @private
   * @returns { region }
   * @memberof UserService
   */
  async _selectRegion({ email }) {
    const regionsAvailableResponse = await AuthenticationServiceApi.checkAvailabilityByRegion({
      email,
    });

    const index = regionsAvailableResponse.findIndex(({ data }) => data?.isAvailable);
    const error = regionsAvailableResponse.find(({ error }) => error)?.error;

    // handle error while trying checking region availability e.g. 429 - too many requests
    // doesn't throw an error in development mode to allow running only one region's backend service
    if (error && currentEnvironment() !== 'development') {
      throw new Error(GENERIC_SERVER_ERROR_MESSAGE);
    }

    const allowedRegion = API.getAPIRegions()[index];

    API.setClientByRegion(allowedRegion);

    return { region: allowedRegion };
  }

  /**
   * Handle access token refresh if token is expiring soon
   * @private
   * @memberof UserService
   */
  async _refreshAccessTokenThatIsExpiringSoon() {
    const hoursBeforeExpiration =
      this.storeState.generalConfig.HOURS_BEFORE_TOKEN_EXPIRATION_TO_REFRESH;

    // Check if token is expiring soon
    const tokenWillExpireSoon = this.tokenExpiration < hoursBeforeExpiration * 60 * 60 * 1000;

    this.logger.debug(`token expiration check`, {
      tokenWillExpireSoon,
      tokenExpiration: this.tokenExpiration,
      hoursBeforeExpiration: hoursBeforeExpiration,
      userId: this.userId,
      organizationId: this.organizationId,
      refreshTokenDetails: this.state?.refreshTokenDetails,
      accessTokenDetails: this.state?.tokenDetails,
    });

    // If token is not expiring soon, do nothing
    // Return true to indicate that token is not expiring soon
    if (!tokenWillExpireSoon) {
      return REFRESH_RESULT.SUCCESS;
    }

    this.logger.info(`token will expire`, {
      userId: this.userId,
      organizationId: this.organizationId,
    });

    const refreshResult = await authService.refreshToken();

    if (refreshResult === REFRESH_RESULT.SUCCESS) {
      this.logger.info('token refreshed successfully', {
        userId: this.userId,
        organizationId: this.organizationId,
      });
    } else if (refreshResult === REFRESH_RESULT.NETWORK_ERROR) {
      this.logger.warn('token refresh failed due to network - keeping session for offline mode', {
        userId: this.userId,
        organizationId: this.organizationId,
      });
    }

    return refreshResult;
  }

  _finishAuthStep() {
    this.dispatch(finishStep({ step: APPLICATION_STEPS.AUTHENTICATION_PROCESS }));
  }

  _forceLogout({ skipApiCall = false, signOutCode = null } = {}) {
    this.dispatch(logout({ skipApiCall, signOutCode }));
  }

  get userId() {
    return this.state.id || null;
  }

  get organizationId() {
    return this.state.user?.organizationId || null;
  }

  get region() {
    return this.state?.region || null;
  }

  // ========== Auth methods =============
  get accessToken() {
    let { accessToken } = this.state;

    return accessToken;
  }

  get tokenExpiration() {
    let { tokenDetails } = this.state;
    return 1000 * tokenDetails?.exp - Date.now();
  }

  get isTokenExpired() {
    let { tokenDetails } = this.state;
    if (tokenDetails && tokenDetails.exp) {
      return 1000 * tokenDetails.exp - Date.now() < 5000;
    }
    return true;
  }

  // Refresh token actions
  get refreshToken() {
    let { refreshToken } = this.state;

    return refreshToken;
  }

  get refreshTokenExpiration() {
    let { refreshTokenDetails } = this.state;
    return 1000 * refreshTokenDetails?.exp - Date.now();
  }

  get isRefreshTokenExpired() {
    let { refreshTokenDetails } = this.state;
    if (refreshTokenDetails && refreshTokenDetails.exp) {
      return 1000 * refreshTokenDetails.exp - Date.now() < 5000;
    }
    return this.isTokenExpired;
  }

  get isAuthenticated() {
    return !this.isRefreshTokenExpired;
  }
}

export default new UserService();
